import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import {
  message,
  Button,
  CustomFilter,
  DatePicker,
  Form,
  Modal,
  Radio,
  Select,
  Table,
  useUpdate,
} from '@maxtropy/components';
import {
  apiV2AreaGetValveSelectPost,
  apiV2AreaList2Post,
  apiV2EnergyConsumptionPlanGasSupplySchedulePost,
  apiV2EnergyConsumptionPlanGasSupplyScheduleStationPost,
  apiV2EnergyConsumptionPlanUpdateValveStatusPost,
  apiV2AreaTaskGetPendingConfirmCountPost,
  apiV2AreaTaskGetConfirmStationListPost,
} from '@maxtropy/device-customer-apis-v2';
import styles from '../index.module.scss';
import { SearchFormValues, GasScheduleApiRow, ValveStatus } from '../types';
import GasScheduleLegend from '../components/GasScheduleLegend';
import ValveStatusChangeLogModal from '../components/ValveStatusChangeLogModal';
import AutoControlConfirmLogModal from '../components/AutoControlConfirmLogModal';
import { Badge, Space } from 'antd';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/utils';
import { useNavigate } from 'react-router-dom';
import AutoControlConfirmModal from './AutoControlConfirmModal';
import { useGasSchedulePendingConfirmRefresh } from '../hook';
import {
  buildAreaRowSpanMap,
  buildStationRowSpanMap,
  buildStationScheduleRows,
  buildStationValveRowSpanMap,
  createAreaGasScheduleColumns,
  createStationGasScheduleColumns,
  GasScheduleView,
  getGasScheduleRowKey,
  getScheduleActionAreaId,
  ScheduleActionRow,
} from './GasScheduleTableColumns';

const GasSchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const hasValveConfigurationPermission = useHasPermission(PermissionsType.B_STATIONHOUSEASSOCIATEDVALVE);
  const hasControlTaskListPermission = useHasPermission(PermissionsType.B_CONTROLTASKLIST);
  const [form] = Form.useForm<SearchFormValues>();
  const [activeView, setActiveView] = useState<GasScheduleView>('station');
  const [filters, setFilters] = useState<SearchFormValues>({
    selectedDate: dayjs(),
  });
  const [recordRow, setRecordRow] = useState<GasScheduleApiRow>();
  const [autoControlConfirmLogOpen, setAutoControlConfirmLogOpen] = useState(false);
  const [autoControlConfirmOpen, setAutoControlConfirmOpen] = useState(false);
  const [update, updateFn] = useUpdate();
  const [updateCount, updateCountFn] = useUpdate();
  const pendingConfirmRefreshTrigger = useGasSchedulePendingConfirmRefresh();

  const { data: areaList } = useRequest(() => apiV2AreaList2Post().then(res => res.list ?? []));
  const { data: valveSelectData } = useRequest(apiV2AreaGetValveSelectPost);
  const buildScheduleRequestParams = () => ({
    startDate: filters.selectedDate ? filters.selectedDate.startOf('day').valueOf() : undefined,
    endDate: filters.selectedDate ? filters.selectedDate.endOf('day').valueOf() : undefined,
    areaIds: filters.areaIds,
    valveIds: filters.valveIds,
    stationRooms: filters.stationRooms,
  });

  const {
    data: areaGasScheduleResponse,
    loading: tableLoading,
    refresh: refreshGasSchedule,
  } = useRequest(() => apiV2EnergyConsumptionPlanGasSupplySchedulePost(buildScheduleRequestParams()), {
    ready: activeView === 'area',
    refreshDeps: [
      activeView,
      filters.areaIds,
      filters.stationRooms,
      update,
      filters.valveIds,
      filters.selectedDate?.valueOf(),
    ],
    pollingInterval: 60_000,
  });

  const {
    data: stationGasScheduleResponse,
    loading: stationTableLoading,
    refresh: refreshStationGasSchedule,
  } = useRequest(() => apiV2EnergyConsumptionPlanGasSupplyScheduleStationPost(buildScheduleRequestParams()), {
    ready: activeView === 'station',
    refreshDeps: [activeView, filters, update],
    pollingInterval: 60_000,
  });

  const { data: pendingConfirmCount } = useRequest(apiV2AreaTaskGetPendingConfirmCountPost, {
    refreshDeps: [updateCount, pendingConfirmRefreshTrigger],
  });

  const { data: scadaSelectData } = useRequest(() =>
    apiV2AreaTaskGetConfirmStationListPost().then(res => res.list ?? [])
  );

  useEffect(() => {
    if (!autoControlConfirmOpen && (pendingConfirmRefreshTrigger! > 0 || pendingConfirmCount?.pendingCount! > 0)) {
      setAutoControlConfirmOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingConfirmRefreshTrigger, pendingConfirmCount]);

  const areaOptions = useMemo(() => areaList?.map(item => ({ label: item.zoneName, value: item.id })), [areaList]);

  const stationOptions = useMemo(
    () => (scadaSelectData ?? []).map(item => ({ label: item.name, value: item.id! })),
    [scadaSelectData]
  );

  const valveOptions = useMemo(
    () =>
      Array.from(
        new Map(
          (valveSelectData?.list ?? [])
            .filter(item => item.name && item.id !== undefined)
            .map(item => [item.id, { label: item.name, value: item.id }])
        ).values()
      ),
    [valveSelectData?.list]
  );

  const highlightStyles = useMemo(
    () => ({
      cellDanger: styles.cellDanger,
      cellWarning: styles.cellWarning,
    }),
    []
  );

  const openValveActionConfirm = useCallback(
    (gasScheduleRow: ScheduleActionRow, nextValveStatus: Exclude<ValveStatus, ValveStatus.Offline>) => {
      const areaId = getScheduleActionAreaId(gasScheduleRow);
      Modal.confirm({
        title: (
          <div>
            是否下发
            <span
              style={{
                color: nextValveStatus === ValveStatus.Open ? 'var(--mx-success-color)' : 'var(--mx-error-color)',
              }}
            >
              {nextValveStatus === ValveStatus.Open ? '开' : '关'}
            </span>
            指令至{gasScheduleRow.deviceName ?? '--'}阀门？
          </div>
        ),
        async onOk() {
          await apiV2EnergyConsumptionPlanUpdateValveStatusPost({
            areaId,
            valveId: gasScheduleRow.deviceId,
            valveStatus: nextValveStatus,
          });
          message.success('指令下发成功');
          if (activeView === 'station') {
            refreshStationGasSchedule();
          } else {
            refreshGasSchedule();
          }
        },
      });
    },
    [activeView, refreshGasSchedule, refreshStationGasSchedule]
  );

  const handleOpenAutoControlConfirm = () => {
    setAutoControlConfirmOpen(true);
  };

  const openValveConfigAutoControl = useCallback((record: ScheduleActionRow, areaId?: number) => {
    if (!areaId || !record.deviceId) return;

    const params = new URLSearchParams({
      autoControlDeviceId: String(record.deviceId),
    });
    if (record.mediumId) {
      params.set('autoControlMediumId', String(record.mediumId));
    }

    window.open(`/area/config/page/valveConfig/${areaId}?${params.toString()}`, '_blank');
  }, []);

  const areaGasScheduleRows = useMemo(() => {
    const rows = areaGasScheduleResponse?.list ?? [];
    if (!filters.stationRooms?.length) return rows;

    return rows.filter(record => record.scadaId !== undefined && filters.stationRooms?.includes(record.scadaId));
  }, [areaGasScheduleResponse?.list, filters.stationRooms]);
  const areaRowSpanMap = useMemo(() => buildAreaRowSpanMap(areaGasScheduleRows), [areaGasScheduleRows]);

  const stationScheduleRows = useMemo(() => {
    const stationList = stationGasScheduleResponse?.list ?? [];
    const filteredStationList = filters.stationRooms?.length
      ? stationList.filter(station => station.scadaId !== undefined && filters.stationRooms?.includes(station.scadaId))
      : stationList;

    return buildStationScheduleRows(filteredStationList);
  }, [stationGasScheduleResponse?.list, filters.stationRooms]);
  const stationRowSpanMap = useMemo(() => buildStationRowSpanMap(stationScheduleRows), [stationScheduleRows]);
  const stationValveRowSpanMap = useMemo(() => buildStationValveRowSpanMap(stationScheduleRows), [stationScheduleRows]);

  const gasScheduleColumns = useMemo(
    () =>
      createAreaGasScheduleColumns({
        areaRowSpanMap,
        highlightStyles,
        timeListClassName: styles.timeList,
        onOpenRecord: setRecordRow,
        onOpenValveActionConfirm: openValveActionConfirm,
        onOpenValveConfigAutoControl: record => openValveConfigAutoControl(record, record.id),
      }),
    [areaRowSpanMap, highlightStyles, openValveActionConfirm, openValveConfigAutoControl]
  );

  const stationScheduleColumns = useMemo(
    () =>
      createStationGasScheduleColumns({
        stationRowSpanMap,
        stationValveRowSpanMap,
        highlightStyles,
        timeListClassName: styles.timeList,
        onOpenRecord: record => setRecordRow(record as GasScheduleApiRow),
        onOpenValveActionConfirm: openValveActionConfirm,
        onOpenValveConfigAutoControl: record => openValveConfigAutoControl(record, record.areaConfigId),
      }),
    [stationRowSpanMap, stationValveRowSpanMap, highlightStyles, openValveActionConfirm, openValveConfigAutoControl]
  );

  return (
    <>
      <div className={styles.page}>
        <CustomFilter<SearchFormValues>
          form={form}
          onFinish={values => {
            setFilters(values);
            updateFn();
          }}
          onReset={() => {
            form.resetFields();
            const nextFilters = { selectedDate: dayjs() };
            form.setFieldValue('selectedDate', nextFilters.selectedDate);
            setFilters(nextFilters);
            updateFn();
          }}
        >
          <Form.Item name="selectedDate" label="时间范围" initialValue={dayjs()}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="areaIds" label="区域名称">
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount={1}
              optionFilterProp="label"
              options={areaOptions}
              placeholder="请选择区域"
            />
          </Form.Item>
          <Form.Item name="stationRooms" label="供气站房">
            <Select
              mode="multiple"
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="请选择站房"
              options={stationOptions}
            />
          </Form.Item>
          <Form.Item name="valveIds" label="供气管道阀门">
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount={1}
              optionFilterProp="label"
              options={valveOptions}
              placeholder="请选择阀门"
            />
          </Form.Item>
        </CustomFilter>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space size={8}>
            {hasValveConfigurationPermission && (
              <Button type="primary" onClick={() => navigate('/energy/usage/gasSchedule/stationValveConfiguration')}>
                站房关联阀门配置
              </Button>
            )}
            {hasControlTaskListPermission && (
              <Button type="primary" onClick={() => navigate('/energy/usage/gasSchedule/controlTaskList')}>
                控制任务列表
              </Button>
            )}
            <Button type="primary" onClick={() => setAutoControlConfirmLogOpen(true)}>
              自动控制确认单日志
            </Button>
            <Badge count={pendingConfirmCount?.pendingCount ?? 0}>
              <Button type="primary" onClick={handleOpenAutoControlConfirm}>
                自动控制确认单
              </Button>
            </Badge>
          </Space>
          <Space size={24}>
            <GasScheduleLegend />
            <Radio.Group
              value={activeView}
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: '站房视角', value: 'station' },
                { label: '区域视角', value: 'area' },
              ]}
              onChange={event => {
                setActiveView(event.target.value);
              }}
            />
          </Space>
        </div>

        {activeView === 'station' && (
          <Table
            rowKey="rowKey"
            columns={stationScheduleColumns}
            dataSource={stationScheduleRows}
            loading={stationTableLoading}
            pagination={false}
            bordered
          />
        )}
        {activeView === 'area' && (
          <Table
            rowKey={getGasScheduleRowKey}
            columns={gasScheduleColumns}
            dataSource={areaGasScheduleRows}
            loading={tableLoading}
            pagination={false}
            bordered
          />
        )}
      </div>

      <ValveStatusChangeLogModal open={!!recordRow} recordRow={recordRow} onCancel={() => setRecordRow(undefined)} />
      <AutoControlConfirmLogModal
        open={autoControlConfirmLogOpen}
        onCancel={() => setAutoControlConfirmLogOpen(false)}
      />
      <AutoControlConfirmModal
        open={autoControlConfirmOpen}
        onCancel={() => setAutoControlConfirmOpen(false)}
        onHandled={updateCountFn}
      />
    </>
  );
};

export default GasSchedulePage;
